import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../../pages/auth/services/auth.service';
import { CookieService } from '../common/cookie.service';
import { TokenInfoKey } from '@/app/shared/constants';

/**
 * API Interceptor
 */
export const ApiInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const authService = inject(AuthService);
  const cookieService = inject(CookieService);
  const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(7)}`;

  // Check if request needs authentication
  const isAuthEndpoint = req.url.includes('/auth/login') || req.url.includes('/auth/register');

  // Clone request with headers
  let modifiedReq = req.clone({
    setHeaders: {
      'X-Request-ID': requestId,
    },
  });

  // Lấy token trực tiếp từ cookie thay vì qua AuthService để tránh khởi tạo sớm
  const accessToken = cookieService.getCookie(TokenInfoKey);

  // Add Authorization header if token exists and not auth endpoint
  if (!isAuthEndpoint && accessToken) {
    modifiedReq = modifiedReq.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        if (authService.hasExpiredAccessToken()) {
          authService.logout(true);
          return throwError(() => error);
        } else {
          // Refresh token and retry request
          return authService.refreshAccessToken().pipe(
            switchMap((loginResponse) => {
              // Update token in storage (AuthService will handle it)
              // Note: AuthService should update the token internally
              // Replay request with new token
              const newReq = modifiedReq.clone({
                setHeaders: {
                  'X-Request-ID': requestId,
                  Authorization: `Bearer ${loginResponse.AccessToken}`,
                },
              });
              return next(newReq);
            }),
            catchError((refreshError) => {
              // If refresh fails, logout
              authService.logout(true);
              return throwError(() => refreshError);
            }),
          );
        }
      } else if (error.status === 403) {
        // Forbidden - insufficient permissions
        router.navigate(['/unauthorized']);
      } else if (error.status === 0) {
        // Network error
        console.error('Network error - please check your connection');
      }

      return throwError(() => error);
    }),
  );
};
